import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import ModalV2 from '../../../componentsv2/Modal';
import Form from '../../../componentsv2/Form';
import Button from '../../../componentsv2/Button';
import Typography from '../../../componentsv2/Typography';
import { CheckboxField, SelectField, TextField } from '../../../componentsv2/Field';
import PhoneNumberWithCountryField from '../../../componentsv2/Field/PhoneNumberWithCountryField';
import { useSnackbar } from '../../../context';
import { useAdminCreateCompany } from '../../../hooks/useAdminCreateCompany';
import { backendCountryValues, getCountries } from '../../../constants/data';
import { CountryKeys, CountryOption, Nullable } from '../../../types';
import { OTHER_PHONE_COUNTRY, phoneCountryCodes } from '../../../utils/helpers';
import { Company } from '../../../types/companyUser';
import { COLORS } from '../../../themev2';

interface CreateCompanyModalProps {
  isOpen: boolean;
  handleClose: () => void;
  t: (key: string) => string;
}

const CreateCompanyModal = ({ isOpen, handleClose, t }: CreateCompanyModalProps) => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const createCompany = useAdminCreateCompany();
  const [createdCompany, setCreatedCompany] = useState<Nullable<Company>>(null);

  const close = () => {
    setCreatedCompany(null);
    handleClose();
  };

  return (
    <ModalV2 isOpen={isOpen} handleClose={close} isAutoHeight>
      <Grid container>
        {createdCompany ? (
          <>
            <Grid item xs={12}>
              <Typography variant="h6">{t('admin:createCompany.successTitle')}</Typography>
            </Grid>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={() => navigate(`/companies/${createdCompany.uuid}`)}>
                {t('admin:createCompany.viewDetail')}
              </Button>
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={12}>
              <Typography variant="h6">{t('admin:createCompany.title')}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Formik
                initialValues={{
                  firstName: '',
                  lastName: '',
                  email: '',
                  phoneNumber: '',
                  phoneNumberCountry: 'mx',
                  companyName: '',
                  country: backendCountryValues.us,
                  sendPasswordEmail: false,
                }}
                validationSchema={Yup.object().shape({
                  firstName: Yup.string().required(t('common:requiredField')),
                  lastName: Yup.string().required(t('common:requiredField')),
                  email: Yup.string().email(t('common:invalidEmail')).required(t('common:requiredField')),
                  phoneNumber: Yup.string().required(t('common:requiredField')),
                  companyName: Yup.string().required(t('common:requiredField')),
                  country: Yup.string().required(t('common:requiredField')),
                })}
                onSubmit={(values) => {
                  const { phoneNumberCountry, ...rest } = values;
                  const dialCode =
                    phoneNumberCountry === OTHER_PHONE_COUNTRY
                      ? ''
                      : `+${phoneCountryCodes[phoneNumberCountry as CountryKeys].code}`;
                  const payload = {
                    ...rest,
                    phoneNumber: values.phoneNumber ? `${dialCode}${values.phoneNumber}` : '',
                  };
                  createCompany.mutate(payload, {
                    onSuccess: (company) => {
                      showSnackbar(t('admin:createCompany.success'), 'success');
                      setCreatedCompany(company);
                    },
                    onError: () => {
                      showSnackbar(t('admin:createCompany.error'), 'error');
                    },
                  });
                }}
              >
                <Form>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2em' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography sx={sectionStyles}>
                          {t('admin:createCompany.sections.company')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField name="companyName" label={t('admin:createCompany.fields.companyName')} />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <SelectField
                          label={t('admin:createCompany.fields.country')}
                          name="country"
                          options={getCountries(t)
                            .sort((a: CountryOption, b: CountryOption) => a.country.localeCompare(b.country))
                            .map(({ country, countryKey }) => ({
                              value: backendCountryValues[countryKey],
                              label: country,
                            }))}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography sx={sectionStyles}>{t('admin:createCompany.sections.owner')}</Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField name="firstName" label={t('admin:createCompany.fields.firstName')} />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField name="lastName" label={t('admin:createCompany.fields.lastName')} />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          name="email"
                          fullWidth
                          label={t('admin:createCompany.fields.email')}
                          autoComplete="off"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <PhoneNumberWithCountryField
                          name="phoneNumber"
                          countryName="phoneNumberCountry"
                          label={t('admin:createCompany.fields.phoneNumber')}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <CheckboxField
                          name="sendPasswordEmail"
                          label={t('admin:createCompany.fields.sendPasswordEmail')}
                        />
                      </Grid>
                      <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button isSubmit isLoading={createCompany.isLoading}>
                          {t('admin:createCompany.submit')}
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                </Form>
              </Formik>
            </Grid>
          </>
        )}
      </Grid>
    </ModalV2>
  );
};

const sectionStyles = {
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '20px',
  color: COLORS.gray[1200],
};

export default CreateCompanyModal;
